import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_SCARE_INTERVAL_SECONDS = 30;
const DEFAULT_SCARE_DURATION_MS = 6000; // fallback if server value missing

// --- Web Audio scare synths (max volume, no external files) ---
let _audioCtx = null;
function getAudioCtx() {
  if (!_audioCtx) {
    try {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (Ctor) _audioCtx = new Ctor();
    } catch { _audioCtx = null; }
  }
  if (_audioCtx && _audioCtx.state === 'suspended') {
    _audioCtx.resume().catch(() => {});
  }
  return _audioCtx;
}

function makeMaster(ctx) {
  const g = ctx.createGain();
  g.gain.value = 1.0; // MAX volume
  g.connect(ctx.destination);
  return g;
}

// --- Distinct scare synths (each sounds very different) ---
function playChainsawScare() {
  const ctx = getAudioCtx();
  if (!ctx) return;
  const master = makeMaster(ctx);
  const now = ctx.currentTime;

  // Master distortion
  const shaper = ctx.createWaveShaper();
  const curve = new Float32Array(2048);
  for (let i = 0; i < 2048; i++) {
    const x = (i / 2048) * 2 - 1;
    curve[i] = Math.tanh(x * 12);
  }
  shaper.curve = curve;
  shaper.oversample = '4x';
  const preGain = ctx.createGain();
  preGain.gain.value = 2.2;
  preGain.connect(shaper).connect(master);

  // Extended rev pattern across 5.7s: idle→REV→hold→dip→REV→hold→REV→sustained→fade
  const engine = ctx.createOscillator();
  engine.type = 'sawtooth';
  engine.frequency.setValueAtTime(90, now);
  engine.frequency.linearRampToValueAtTime(230, now + 0.4);
  engine.frequency.linearRampToValueAtTime(210, now + 1.1);
  engine.frequency.linearRampToValueAtTime(105, now + 1.5);
  engine.frequency.linearRampToValueAtTime(260, now + 1.95);
  engine.frequency.linearRampToValueAtTime(230, now + 2.8);
  engine.frequency.linearRampToValueAtTime(140, now + 3.2);
  engine.frequency.linearRampToValueAtTime(270, now + 3.65);
  engine.frequency.linearRampToValueAtTime(240, now + 4.8);
  engine.frequency.linearRampToValueAtTime(130, now + 5.5);

  const engine2 = ctx.createOscillator();
  engine2.type = 'sawtooth';
  engine2.frequency.setValueAtTime(135, now);
  engine2.frequency.linearRampToValueAtTime(345, now + 0.4);
  engine2.frequency.linearRampToValueAtTime(315, now + 1.1);
  engine2.frequency.linearRampToValueAtTime(155, now + 1.5);
  engine2.frequency.linearRampToValueAtTime(390, now + 1.95);
  engine2.frequency.linearRampToValueAtTime(345, now + 2.8);
  engine2.frequency.linearRampToValueAtTime(210, now + 3.2);
  engine2.frequency.linearRampToValueAtTime(405, now + 3.65);
  engine2.frequency.linearRampToValueAtTime(360, now + 4.8);
  engine2.frequency.linearRampToValueAtTime(195, now + 5.5);

  const chugLfo = ctx.createOscillator();
  chugLfo.type = 'sawtooth';
  chugLfo.frequency.setValueAtTime(28, now);
  chugLfo.frequency.linearRampToValueAtTime(52, now + 0.4);
  chugLfo.frequency.linearRampToValueAtTime(46, now + 1.5);
  chugLfo.frequency.linearRampToValueAtTime(60, now + 1.95);
  chugLfo.frequency.linearRampToValueAtTime(50, now + 3.2);
  chugLfo.frequency.linearRampToValueAtTime(62, now + 3.65);
  chugLfo.frequency.linearRampToValueAtTime(48, now + 4.8);
  chugLfo.frequency.linearRampToValueAtTime(30, now + 5.5);
  const chugDepth = ctx.createGain();
  chugDepth.gain.value = 0.55;
  const chugOffset = ctx.createConstantSource();
  chugOffset.offset.value = 0.45;
  chugOffset.start(now);
  const chugAmp = ctx.createGain();
  chugAmp.gain.value = 0;
  chugLfo.connect(chugDepth).connect(chugAmp.gain);
  chugOffset.connect(chugAmp.gain);

  const whine = ctx.createOscillator();
  whine.type = 'square';
  whine.frequency.setValueAtTime(1800, now);
  whine.frequency.linearRampToValueAtTime(2600, now + 0.4);
  whine.frequency.linearRampToValueAtTime(2400, now + 1.1);
  whine.frequency.linearRampToValueAtTime(1900, now + 1.5);
  whine.frequency.linearRampToValueAtTime(2800, now + 1.95);
  whine.frequency.linearRampToValueAtTime(2500, now + 3.2);
  whine.frequency.linearRampToValueAtTime(2900, now + 3.65);
  whine.frequency.linearRampToValueAtTime(2600, now + 4.8);
  whine.frequency.linearRampToValueAtTime(1700, now + 5.5);
  const whineFilter = ctx.createBiquadFilter();
  whineFilter.type = 'highpass';
  whineFilter.frequency.value = 1500;
  const whineGain = ctx.createGain();
  whineGain.gain.setValueAtTime(0, now);
  whineGain.gain.linearRampToValueAtTime(0.35, now + 0.5);
  whineGain.gain.linearRampToValueAtTime(0.4, now + 4.5);
  whineGain.gain.linearRampToValueAtTime(0, now + 5.55);

  const amp = ctx.createGain();
  amp.gain.setValueAtTime(0, now);
  amp.gain.linearRampToValueAtTime(0.9, now + 0.05);
  amp.gain.linearRampToValueAtTime(1.0, now + 0.5);
  amp.gain.linearRampToValueAtTime(1.0, now + 5.1);
  amp.gain.linearRampToValueAtTime(0, now + 5.6);

  engine.connect(chugAmp);
  engine2.connect(chugAmp);
  chugAmp.connect(amp).connect(preGain);
  whine.connect(whineFilter).connect(whineGain).connect(master);

  const stopAt = now + 5.7;
  engine.start(now); engine2.start(now); chugLfo.start(now); whine.start(now);
  engine.stop(stopAt); engine2.stop(stopAt); chugLfo.stop(stopAt); whine.stop(stopAt);
  chugOffset.stop(stopAt);
}

function playPigSquealScare() {
  // LAYERED pig scare: Insidious sting + real pig squeal on top — stretched to ~5.7s
  const ctx = getAudioCtx();
  if (!ctx) return;
  const master = makeMaster(ctx);
  const now = ctx.currentTime;

  // ---- Layer A: Insidious sting (extended) ----
  const sub = ctx.createOscillator();
  sub.type = 'sine';
  sub.frequency.setValueAtTime(55, now);
  sub.frequency.exponentialRampToValueAtTime(28, now + 2.2);
  const subGain = ctx.createGain();
  subGain.gain.setValueAtTime(0, now);
  subGain.gain.linearRampToValueAtTime(1.0, now + 0.008);
  subGain.gain.exponentialRampToValueAtTime(0.001, now + 3.0);
  sub.connect(subGain).connect(master);
  sub.start(now);
  sub.stop(now + 3.1);

  const clusterGain = ctx.createGain();
  clusterGain.gain.setValueAtTime(0, now);
  clusterGain.gain.linearRampToValueAtTime(0.75, now + 0.012);
  clusterGain.gain.linearRampToValueAtTime(0.70, now + 3.8);
  clusterGain.gain.linearRampToValueAtTime(0.55, now + 5.2);
  clusterGain.gain.linearRampToValueAtTime(0, now + 5.7);
  clusterGain.connect(master);
  const cluster = [
    { freq: 2400, detune: 0 },
    { freq: 2400, detune: 45 },
    { freq: 3200, detune: 12 },
    { freq: 1800, detune: -25 },
  ];
  cluster.forEach(({ freq, detune }) => {
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.detune.value = detune;
    osc.frequency.setValueAtTime(freq, now);
    osc.frequency.linearRampToValueAtTime(freq * 0.9, now + 5.7);
    const trem = ctx.createOscillator();
    trem.frequency.value = 9;
    const tremGain = ctx.createGain();
    tremGain.gain.value = 22;
    trem.connect(tremGain).connect(osc.frequency);
    osc.connect(clusterGain);
    osc.start(now);
    trem.start(now);
    osc.stop(now + 5.75);
    trem.stop(now + 5.75);
  });

  // Bass thumps at 0.7s, 2.2s, 3.8s for repeated punches
  [0.7, 2.2, 3.8].forEach(t => {
    const thump = ctx.createOscillator();
    thump.type = 'sine';
    thump.frequency.setValueAtTime(75, now + t);
    thump.frequency.exponentialRampToValueAtTime(32, now + t + 0.5);
    const thumpGain = ctx.createGain();
    thumpGain.gain.setValueAtTime(0, now + t);
    thumpGain.gain.linearRampToValueAtTime(0.9, now + t + 0.008);
    thumpGain.gain.exponentialRampToValueAtTime(0.001, now + t + 0.65);
    thump.connect(thumpGain).connect(master);
    thump.start(now + t);
    thump.stop(now + t + 0.7);
  });

  // ---- Layer B: Pig squeal bursts (6 total, spread across the duration) ----
  const squealShaper = ctx.createWaveShaper();
  const sqCurve = new Float32Array(1024);
  for (let i = 0; i < 1024; i++) { const x = (i / 1024) * 2 - 1; sqCurve[i] = Math.sign(x) * Math.pow(Math.abs(x), 0.4); }
  squealShaper.curve = sqCurve;
  const squealBus = ctx.createGain();
  squealBus.gain.value = 0.55;
  squealShaper.connect(squealBus).connect(master);
  const squealBursts = [
    { at: now + 0.35, dur: 0.45, base: 1200 },
    { at: now + 0.9,  dur: 0.35, base: 1500 },
    { at: now + 1.3,  dur: 0.55, base: 1300 },
    { at: now + 2.4,  dur: 0.5,  base: 1400 },
    { at: now + 3.3,  dur: 0.45, base: 1250 },
    { at: now + 4.4,  dur: 0.6,  base: 1350 },
  ];
  squealBursts.forEach(b => {
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(b.base, b.at);
    osc.frequency.linearRampToValueAtTime(b.base + 400, b.at + b.dur * 0.35);
    osc.frequency.linearRampToValueAtTime(b.base - 200, b.at + b.dur);
    const wob = ctx.createOscillator();
    wob.frequency.value = 20;
    const wobGain = ctx.createGain();
    wobGain.gain.value = 180;
    wob.connect(wobGain).connect(osc.frequency);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, b.at);
    g.gain.linearRampToValueAtTime(1.0, b.at + 0.03);
    g.gain.linearRampToValueAtTime(0.85, b.at + b.dur * 0.7);
    g.gain.linearRampToValueAtTime(0, b.at + b.dur);
    osc.connect(g).connect(squealShaper);
    osc.start(b.at);
    wob.start(b.at);
    osc.stop(b.at + b.dur + 0.05);
    wob.stop(b.at + b.dur + 0.05);
  });
}

function playClownLaughScare() {
  // LAYERED clown scare — stretched to ~5.7s with more laugh syllables + longer screech
  const ctx = getAudioCtx();
  if (!ctx) return;
  const master = makeMaster(ctx);
  const now = ctx.currentTime;

  // ---- Layer A: 14-syllable staccato laugh spread across ~5.5s ----
  const syllables = [0, 0.28, 0.55, 0.82, 1.15, 1.5, 1.95, 2.4, 2.8, 3.2, 3.65, 4.15, 4.6, 5.1];
  const pitches   = [280, 240, 260, 230, 200, 220, 180, 200, 230, 210, 190, 250, 220, 170];
  syllables.forEach((t, i) => {
    const start = now + t;
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(pitches[i], start);
    osc.frequency.linearRampToValueAtTime(pitches[i] * 1.15, start + 0.05);
    osc.frequency.linearRampToValueAtTime(pitches[i] * 0.7, start + 0.2);
    const shaper = ctx.createWaveShaper();
    const curve = new Float32Array(512);
    for (let k = 0; k < 512; k++) { const x = (k / 512) * 2 - 1; curve[k] = Math.sign(x) * Math.pow(Math.abs(x), 0.5); }
    shaper.curve = curve;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, start);
    g.gain.linearRampToValueAtTime(0.85, start + 0.03);
    g.gain.linearRampToValueAtTime(0.75, start + 0.15);
    g.gain.linearRampToValueAtTime(0, start + 0.24);
    osc.connect(shaper).connect(g).connect(master);
    osc.start(start);
    osc.stop(start + 0.28);
  });

  // ---- Layer B: sustained horror-strings screech underneath (5.5s) ----
  const screechGain = ctx.createGain();
  screechGain.gain.setValueAtTime(0, now);
  screechGain.gain.linearRampToValueAtTime(0.55, now + 0.15);
  screechGain.gain.linearRampToValueAtTime(0.6, now + 3.5);
  screechGain.gain.linearRampToValueAtTime(0.5, now + 5.0);
  screechGain.gain.linearRampToValueAtTime(0, now + 5.6);
  const screechHP = ctx.createBiquadFilter();
  screechHP.type = 'highpass';
  screechHP.frequency.value = 1400;
  screechGain.connect(screechHP).connect(master);

  const screechVoices = [
    { freq: 1600, detune: 0, rise: 260 },
    { freq: 1600, detune: 38, rise: 220 },
    { freq: 2200, detune: -18, rise: 300 },
    { freq: 2800, detune: 22, rise: 380 },
  ];
  screechVoices.forEach(({ freq, detune, rise }) => {
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    osc.detune.value = detune;
    osc.frequency.setValueAtTime(freq, now);
    osc.frequency.linearRampToValueAtTime(freq + rise, now + 3.5);
    osc.frequency.linearRampToValueAtTime(freq + rise * 0.5, now + 5.6);
    const trem = ctx.createOscillator();
    trem.frequency.value = 14;
    const tremGain = ctx.createGain();
    tremGain.gain.value = 30;
    trem.connect(tremGain).connect(osc.frequency);
    osc.connect(screechGain);
    osc.start(now);
    trem.start(now);
    osc.stop(now + 5.65);
    trem.stop(now + 5.65);
  });
}

const SOUND_FNS = {
  pig: playPigSquealScare,
  clown: playClownLaughScare,
  chainsaw: playChainsawScare,
};
function playByKey(key) {
  const fn = SOUND_FNS[key] || playPigSquealScare;
  try { fn(); } catch {}
}

// --- Non-halloween normal scares (unchanged) ---
const NORMAL_SCARE_EFFECTS = [
  { text: "TIME'S UP!", sub: '...for no reason in particular. Just here to stress you out.', textJa: '時間切れ！', subJa: '…特に理由はありません。ただあなたを焦らせたいだけ。', bg: 'radial-gradient(circle, rgba(227,37,16,0.95) 0%, rgba(0,0,0,0.98) 70%)' },
  { text: 'TOO LATE', sub: 'Late for what? Nothing. We just like countdowns.', textJa: '手遅れ', subJa: '何に遅れた？何も。カウントダウンが好きなだけ。', bg: 'radial-gradient(circle, rgba(180,0,0,0.95) 0%, rgba(0,0,0,0.98) 70%)' },
  { text: 'BOOM', sub: "Just kidding. But your heart skipped a beat, didn't it?", textJa: 'ドカン！', subJa: '冗談です。でもドキッとしたでしょ？', bg: 'radial-gradient(circle, rgba(255,120,0,0.9) 0%, rgba(0,0,0,0.98) 60%)' },
  { text: 'GAME OVER', sub: 'Not really. This countdown exists purely to stress you out.', textJa: 'ゲームオーバー', subJa: '…なんてね。このカウントダウンはただ煽るためだけにあります。', bg: 'radial-gradient(circle, rgba(200,20,20,0.95) 0%, rgba(0,0,0,0.98) 70%)' },
  { text: 'TIMES UP', sub: "Relax... it's just a countdown. Or is it?", textJa: 'タイムアップ', subJa: '落ち着いて…ただのカウントダウンです。本当に？', bg: 'radial-gradient(circle, rgba(100,0,0,0.95) 0%, rgba(20,0,0,0.98) 70%)' },
];

// --- Matched Halloween bundles (text + gifIndex + soundKey + bg — always coherent) ---
const HALLOWEEN_SCARES = [
  {
    key: 'pig', gifIndex: 0, soundKey: 'pig',
    text: 'GOTCHA!',
    sub: 'The pig is watching. Always watching.',
    textJa: 'つかまえた！',
    subJa: '豚頭がずっと見ています。ずっと。',
    bg: 'radial-gradient(circle, rgba(180,0,0,0.96) 0%, rgba(0,0,0,0.99) 65%)',
  },
  {
    key: 'clown', gifIndex: 1, soundKey: 'clown',
    text: "IT'S BEHIND YOU",
    sub: "Don't turn around... on second thought, please do.",
    textJa: 'うしろにいる…',
    subJa: '振り返らないで…いや、やっぱり振り返って。',
    bg: 'radial-gradient(circle, rgba(255,140,0,0.95) 0%, rgba(20,0,0,0.99) 60%)',
  },
  {
    key: 'chainsaw', gifIndex: 2, soundKey: 'chainsaw',
    text: 'HEAR THAT CHAINSAW?',
    sub: "It's getting closer. Book before you become the room.",
    textJa: 'チェーンソーの音…',
    subJa: 'だんだん近づいてくる。逃げ切れるうちに予約を。',
    bg: 'radial-gradient(circle, rgba(200,20,20,0.97) 0%, rgba(0,0,0,0.99) 70%)',
  },
];

export default function DoomsdayCountdown() {
  const { lang } = useLanguage();
  const isJa = lang === 'ja';
  const [seconds, setSeconds] = useState(DEFAULT_SCARE_INTERVAL_SECONDS);
  const [scare, setScare] = useState(null);
  const [effectIdx, setEffectIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const [siteCfg, setSiteCfg] = useState(null);
  const intervalRef = useRef(null);
  const scareTimeoutRef = useRef(null);
  const scareFiringRef = useRef(false); // guards against effect re-runs mid-scare

  useEffect(() => {
    axios.get(`${API}/api/settings/site`).then(r => setSiteCfg(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const prime = () => { getAudioCtx(); };
    window.addEventListener('pointerdown', prime, { once: true, passive: true });
    window.addEventListener('keydown', prime, { once: true, passive: true });
    return () => {
      window.removeEventListener('pointerdown', prime);
      window.removeEventListener('keydown', prime);
    };
  }, []);

  const halloweenMode = !!(siteCfg && siteCfg.halloween_enabled);
  const scareInterval = Math.max(10, Math.min(300, parseInt(siteCfg?.halloween_scare_seconds, 10) || DEFAULT_SCARE_INTERVAL_SECONDS));
  const scareDurationMs = Math.max(3000, Math.min(10000, parseInt(siteCfg?.halloween_scare_duration_ms, 10) || DEFAULT_SCARE_DURATION_MS));
  const halloweenGifs = halloweenMode && Array.isArray(siteCfg?.halloween_gifs) ? siteCfg.halloween_gifs : [];
  // Per-visitor LIFETIME cap — stop scaring the same guest after N scares total
  // (persisted in localStorage, never resets). Default 3 scares per device, forever.
  const scareMax = Math.max(1, parseInt(siteCfg?.halloween_scare_max_per_visitor, 10) || 3);
  const SCARE_KEY = 'oki.scares.total';
  const readScareCount = () => {
    try { return Math.max(0, parseInt(localStorage.getItem(SCARE_KEY), 10) || 0); } catch { return 0; }
  };
  const bumpScareCount = () => {
    try {
      const next = readScareCount() + 1;
      localStorage.setItem(SCARE_KEY, String(next));
      // Cleanup legacy per-day keys from older builds
      Object.keys(localStorage).forEach(k => { if (k.startsWith('oki.scares.') && k !== SCARE_KEY) localStorage.removeItem(k); });
      return next;
    } catch { return 0; }
  };

  // Admin / scanner suppression — never scare staff. Detects:
  //   • an admin JWT in localStorage ('access_token')
  //   • a scanner door PIN unlock ('oki.scanner.unlocked' or 'oki_scanner_pin')
  //   • the current URL being /admin* or /scan*
  const isStaffContext = () => {
    try {
      if (typeof window === 'undefined') return false;
      const path = window.location?.pathname || '';
      if (path.startsWith('/admin') || path.startsWith('/scan')) return true;
      if (localStorage.getItem('access_token')) return true;
      if (localStorage.getItem('oki.scanner.unlocked') === '1') return true;
      return false;
    } catch { return false; }
  };

  // Countdown starts 30s after mount — unless admin/scanner is active (never for staff)
  useEffect(() => {
    if (isStaffContext()) return; // no scares at all for staff
    const delay = setTimeout(() => {
      setVisible(true);
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        setSeconds(prev => (prev <= 1 ? 0 : prev - 1));
      }, 1000);
    }, 30000);
    return () => {
      clearTimeout(delay);
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (scareTimeoutRef.current) clearTimeout(scareTimeoutRef.current);
    };
  }, []);

  // If checkout starts mid-scare, kill the current scare instantly so payment UI isn't disrupted
  useEffect(() => {
    const onPause = () => {
      if (scareTimeoutRef.current) { clearTimeout(scareTimeoutRef.current); scareTimeoutRef.current = null; }
      setScare(null);
      scareFiringRef.current = false;
      setSeconds(scareInterval);
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        setSeconds(prev => (prev <= 1 ? 0 : prev - 1));
      }, 1000);
      document.body.classList.remove('doomsday-shake');
    };
    window.addEventListener('hh-pause-scares', onPause);
    return () => window.removeEventListener('hh-pause-scares', onPause);
  }, [scareInterval]);

  useEffect(() => {
    // Reset the fire-guard whenever countdown moves away from 0
    if (seconds !== 0) {
      scareFiringRef.current = false;
      return;
    }
    if (scareFiringRef.current) return; // already firing this scare
    // STAFF SUPPRESSION — admin logged in or on scanner/admin page: never scare
    if (isStaffContext()) {
      scareFiringRef.current = false;
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      return;
    }
    // Guest checkout guard — pause scares while they're on payment / QR screens
    if (typeof window !== 'undefined' && window.__HH_PAUSE_SCARES) {
      scareFiringRef.current = false;
      setSeconds(scareInterval);
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        setSeconds(prev => (prev <= 1 ? 0 : prev - 1));
      }, 1000);
      return;
    }
    // Per-visitor cap — after `scareMax` scares today, quietly stop (no more scares
    // for this device/browser until the calendar rolls over). Keeps the countdown
    // ticker technically alive but never fires again this session.
    if (readScareCount() >= scareMax) {
      scareFiringRef.current = false;
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      return;
    }
    scareFiringRef.current = true;

    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }

    let bundle;
    let gifOverlay = null;
    let soundKey = null;
    if (halloweenMode && HALLOWEEN_SCARES.length > 0) {
      bundle = HALLOWEEN_SCARES[effectIdx % HALLOWEEN_SCARES.length];
      // Grab the matching gif by its bundle-defined index, fallback to first
      gifOverlay = halloweenGifs[bundle.gifIndex] || halloweenGifs[0] || null;
      soundKey = bundle.soundKey;
    } else {
      bundle = NORMAL_SCARE_EFFECTS[effectIdx % NORMAL_SCARE_EFFECTS.length];
    }
    setScare({ ...bundle, gifOverlay });

    if (halloweenMode && soundKey) {
      playByKey(soundKey);
      axios.post(`${API}/api/track/halloween-event`, { event: 'scare_fired' }).catch(() => {});
      // Un-dismiss the banner so it re-appears every time a scare fires
      try { window.dispatchEvent(new CustomEvent('halloween-scare-fired', { detail: { key: bundle.key } })); } catch {}
    }
    // Bump the per-visitor cap counter — after 3, this visitor gets no more scares today
    bumpScareCount();

    document.body.classList.add('doomsday-shake');
    setTimeout(() => document.body.classList.remove('doomsday-shake'), 800);

    if (scareTimeoutRef.current) clearTimeout(scareTimeoutRef.current);
    scareTimeoutRef.current = setTimeout(() => {
      // End scare + immediately restart countdown
      setScare(null);
      setEffectIdx(prev => prev + 1);
      setSeconds(scareInterval);
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        setSeconds(prev => (prev <= 1 ? 0 : prev - 1));
      }, 1000);
      scareTimeoutRef.current = null;
    }, scareDurationMs);
  }, [seconds, effectIdx, halloweenMode, halloweenGifs, scareInterval, scareDurationMs]);

  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  const timeStr = `${min}:${sec.toString().padStart(2, '0')}`;
  const isUrgent = seconds <= 10;
  const isCritical = seconds <= 5;

  return (
    <>
      <style>{`
        @keyframes doomsday-shake {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          10% { transform: translate(-8px, -4px) rotate(-1deg); }
          20% { transform: translate(8px, 4px) rotate(1deg); }
          30% { transform: translate(-6px, 2px) rotate(-0.5deg); }
          40% { transform: translate(6px, -2px) rotate(0.5deg); }
          50% { transform: translate(-4px, 4px) rotate(-1deg); }
          60% { transform: translate(4px, -4px) rotate(1deg); }
          70% { transform: translate(-2px, 2px) rotate(0deg); }
          80% { transform: translate(2px, -2px) rotate(0deg); }
          90% { transform: translate(-1px, 1px) rotate(0deg); }
        }
        .doomsday-shake { animation: doomsday-shake 0.8s ease-in-out; }
        @keyframes hallo-scare-in {
          0% { transform: scale(0.4) rotate(-6deg); opacity: 0; filter: blur(20px); }
          40% { transform: scale(1.15) rotate(3deg); opacity: 1; filter: blur(0); }
          100% { transform: scale(1) rotate(0deg); opacity: 1; filter: blur(0); }
        }
        .hallo-scare-gif {
          animation: hallo-scare-in 0.35s ease-out;
          filter: contrast(1.25) saturate(1.1) brightness(0.95) drop-shadow(0 0 40px rgba(255,60,0,0.9)) drop-shadow(0 0 80px rgba(200,0,0,0.7));
        }
      `}</style>

      {visible && (() => {
        const slot = document.getElementById('doomsday-header-slot');
        if (!slot) return null;
        return createPortal(
          <motion.div
            data-testid="doomsday-countdown"
            className="pointer-events-none select-none mr-1"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={isCritical ? { opacity: 1, scale: [1, 1.08, 1] } : { opacity: 1, scale: 1 }}
            transition={isCritical ? { duration: 0.5, repeat: Infinity } : { duration: 0.5 }}
          >
            <div className={`relative flex items-center gap-1.5 px-2.5 py-1 rounded-full border backdrop-blur-md transition-all duration-300 ${
              isCritical ? 'bg-red-900/80 border-red-500/80 shadow-[0_0_20px_rgba(255,0,0,0.5)]'
              : isUrgent ? 'bg-red-900/50 border-red-600/50 shadow-[0_0_14px_rgba(227,37,16,0.3)]'
              : 'bg-black/60 border-neutral-700/50 shadow-[0_0_10px_rgba(227,37,16,0.15)]'
            }`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isCritical ? 'bg-red-400 animate-ping' : isUrgent ? 'bg-red-500 animate-pulse' : 'bg-brand'}`} />
              <span className={`font-mono text-[11px] sm:text-xs font-bold tracking-[0.15em] transition-colors duration-300 ${
                isCritical ? 'text-red-300' : isUrgent ? 'text-red-400' : 'text-white'
              }`}>
                {timeStr}
              </span>
            </div>
          </motion.div>,
          slot
        );
      })()}

      {visible && isUrgent && !scare && (
        <motion.div
          className="fixed inset-0 pointer-events-none z-[9990]"
          animate={{ opacity: [0, 0.08, 0] }}
          transition={{ duration: 1, repeat: Infinity }}
          style={{ background: halloweenMode
            ? 'radial-gradient(circle, rgba(255,120,0,0.35) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(227,37,16,0.3) 0%, transparent 70%)' }}
        />
      )}

      <AnimatePresence>
        {scare && (
          <motion.div
            data-testid="doomsday-scare"
            data-scare-key={scare.key || ''}
            className="fixed inset-0 z-[99999] flex flex-col items-center justify-center pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            style={{ background: scare.bg }}
          >
            {scare.gifOverlay && (
              <img
                data-testid="doomsday-scare-gif"
                data-scare-gif-label={scare.gifOverlay.label || ''}
                src={scare.gifOverlay.url}
                alt={scare.gifOverlay.label || 'jump scare'}
                className="hallo-scare-gif absolute inset-0 w-full h-full object-cover opacity-95 mix-blend-screen"
                style={{ zIndex: 0 }}
              />
            )}
            <motion.h1
              className="relative font-heading text-6xl sm:text-8xl lg:text-9xl uppercase text-white text-center"
              initial={{ scale: 3, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              style={{
                textShadow: halloweenMode
                  ? '0 0 60px rgba(255,120,0,0.95), 0 0 120px rgba(255,60,0,0.6), 0 0 200px rgba(120,0,0,0.6)'
                  : '0 0 60px rgba(255,0,0,0.8), 0 0 120px rgba(255,0,0,0.4)',
                zIndex: 1,
              }}
            >
              {isJa ? scare.textJa : scare.text}
            </motion.h1>
            <motion.p
              className="relative font-sans text-lg sm:text-xl text-red-200/85 mt-4 text-center px-6 max-w-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.3 }}
              style={{ zIndex: 1, textShadow: '0 2px 12px rgba(0,0,0,0.9)' }}
            >
              {isJa ? scare.subJa : scare.sub}
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
